import { X } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { Button } from ".";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "./dialog";

interface StepProps {
	/** Return value of boolean to indicates whether to proceed to the next step */
	onClick: () => boolean | Promise<boolean>;
	content: ReactNode;
	text: string;
}

interface FullPageDialogProps {
	open: boolean;
	setOpen: (newOpen: boolean) => void;
	steps: StepProps[];
	title: string;
	triggerButton?: ReactNode;
	showCancelButton?: boolean;
}

/** Children component will be the content of the dialog */
export const FullPageDialog = ({
	open,
	setOpen,
	steps,
	title,
	triggerButton,
	showCancelButton = false,
}: FullPageDialogProps) => {
	const [currentStep, setCurrentStep] = useState(0);

	const currentStepAction = async () => {
		const shouldContinue = await steps[currentStep].onClick();
		if (shouldContinue) {
			if (currentStep + 1 === steps.length) {
				setOpen(false);
			} else {
				setCurrentStep((currentStep) => currentStep + 1);
			}
		}
	};

	useEffect(() => {
		setCurrentStep(0);
	}, [open]);

	return (
		<Dialog open={open} onOpenChange={setOpen} modal={true}>
			{triggerButton}
			<DialogContent
				className="data-[state=open]:animate-contentShow max-w-none p-0 h-full translate-x-[-50%] translate-y-[-50%] bg-white shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none gap-0 grid-rows-[3rem_auto_3.5rem] sm:rounded-none"
				showCloseButton={false}
			>
				<div className="flex px-10 py-3 border-b-2 items-center">
					<DialogClose className="ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400 mr-5">
						<X className="h-4 w-4" />
						<span className="sr-only">Close</span>
					</DialogClose>
					<DialogTitle className="text-base font-semibold border-l-2 border-gray-300 pl-5">
						{title}
					</DialogTitle>
				</div>
				<div className="px-10 py-6 overflow-y-auto">
					{steps[currentStep].content}
				</div>
				<div className="flex justify-end px-10 py-3 border-t-2 items-center">
					{showCancelButton && (
						<DialogClose asChild>
							<Button className="mr-4" size="sm" variant="secondary">
								Cancel
							</Button>
						</DialogClose>
					)}
					<Button size="sm" onClick={currentStepAction}>
						{steps[currentStep].text}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};
